import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTooltip } from '@mylighthouse/prism-ember';
import { tKey } from 'ember-intl';
import {
  EDIT_RECOMMENDED_PRICE_BUTTON_TYPE,
  RECOMMENDATIONACTIONS_ACTIONS,
} from 'lumen/utils/constants/pricing-assistant-constants';

interface HotelPricingAssistantRecommendationActionButtonTooltipSignature {
  Args: {
    buttonType:
      | (typeof RECOMMENDATIONACTIONS_ACTIONS)[keyof typeof RECOMMENDATIONACTIONS_ACTIONS]
      | typeof EDIT_RECOMMENDED_PRICE_BUTTON_TYPE;
    showTooltipIfActionButtonsEnabled?: boolean;
  };
}

export default class HotelPricingAssistantRecommendationActionButtonTooltip extends Component<HotelPricingAssistantRecommendationActionButtonTooltipSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get actionButtonInfoTooltipMessage() {
    if (this.actionButtonInfoTooltipMessages.messageKey) {
      return this.intl.t(this.actionButtonInfoTooltipMessages.messageKey);
    }

    return '';
  }

  // The content of the PA action button tooltip depends on the scenario. These are the possible scenarios:
  // * if PMS SS is available: tooltip contains generic info like "Enable 1 click changes by connecting your PMS."
  // * in case that PMS SS is in progress: tooltip contains Data validation in progress message
  // * if PMS is integrated & user has edit rights (so also view rights): tooltip contains button action like Send or Snooze
  // * if PMS is integrated & user has no edit rights: tooltip contains button action + notification that this is for admins only
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get actionButtonInfoTooltipMessages() {
    const actionButtonInfoTooltipMessages: {
      messageKey?: string;
      titleKey?: string;
    } = {};

    if (this.pricingAssistantPmsSs.isPricingAssistantPMSSelfServiceAvailable) {
      actionButtonInfoTooltipMessages.messageKey = tKey(
        'pricing_assistant.self_service.disabled_buttons.message',
      );
    } else if (
      this.pricingAssistantPmsSs.isPricingAssistantPMSSelfServiceInProgress
    ) {
      actionButtonInfoTooltipMessages.titleKey =
        'pricing_assistant.self_service.data_validation.title';
      actionButtonInfoTooltipMessages.messageKey =
        'pricing_assistant.self_service.data_validation.content';
    } else if (
      this.showTooltipIfActionButtonsEnabled &&
      this.access.hasPricingAssistantEditRights
    ) {
      if (this.args.buttonType === RECOMMENDATIONACTIONS_ACTIONS.SNOOZED) {
        actionButtonInfoTooltipMessages.messageKey = tKey(
          'pricing_assistant.day_tile.snooze.cta.tooltip',
        );
      } else if (
        this.args.buttonType === RECOMMENDATIONACTIONS_ACTIONS.ACCEPTED
      ) {
        actionButtonInfoTooltipMessages.messageKey = tKey(
          'pricing_assistant.day_tile.push.cta.tooltip',
        );
      } else if (this.args.buttonType === EDIT_RECOMMENDED_PRICE_BUTTON_TYPE) {
        actionButtonInfoTooltipMessages.messageKey = tKey(
          'pricing_assistant.day_tile.edit_my_price.cta.tooltip',
        );
      }
    } else if (
      !this.access.hasPricingAssistantEditRights &&
      this.access.hasPricingAssistantViewRights
    ) {
      if (this.args.buttonType === RECOMMENDATIONACTIONS_ACTIONS.SNOOZED) {
        actionButtonInfoTooltipMessages.messageKey = tKey(
          'pricing_assistant.snooze.cta.non_admin',
        );
      } else if (
        this.args.buttonType === RECOMMENDATIONACTIONS_ACTIONS.ACCEPTED
      ) {
        actionButtonInfoTooltipMessages.messageKey = tKey(
          'pricing_assistant.push.cta.non_admin',
        );
      } else if (this.args.buttonType === EDIT_RECOMMENDED_PRICE_BUTTON_TYPE) {
        actionButtonInfoTooltipMessages.messageKey = tKey(
          'pricing_assistant.edit_my_price.cta.non_admin',
        );
      }
    }

    return actionButtonInfoTooltipMessages;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get actionButtonInfoTooltipTitle() {
    if (this.actionButtonInfoTooltipMessages.titleKey) {
      return this.intl.t(this.actionButtonInfoTooltipMessages.titleKey);
    }

    return '';
  }

  // By default, we show tooltips on both enabled/disabled recommendation action buttons.
  // Exception: Pricing tab, calendar set price button. We set this flag to false in exception cases.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showTooltipIfActionButtonsEnabled() {
    return this.args.showTooltipIfActionButtonsEnabled ?? true;
  }

  <template>
    {{#if this.actionButtonInfoTooltipMessage}}
      <PrismTooltip
        @text={{this.actionButtonInfoTooltipMessage}}
        @title={{this.actionButtonInfoTooltipTitle}}
      />
    {{/if}}
  </template>
}
